import { useState } from "react";
import { AlertTriangle, CheckCircle2, Sprout, Stethoscope, Download, Printer, RotateCcw, ChevronDown, ShieldCheck, ArrowRight } from "lucide-react";
import { DIMS, PC, STATUS } from "@/components/compass/dims";
import { InteractiveCompass } from "@/components/compass/InteractiveCompass";

function Finding({ i }) {
  return (
    <li className="rounded-2xl bg-white/80 p-3.5">
      <p className="text-sm font-extrabold leading-snug" style={{ color: PC.ink }}>{i.reported}</p>
      {i.why && <p className="mt-1 text-[13px] leading-snug" style={{ color: PC.muted }}>{i.why}</p>}
      {i.next && <p className="mt-1.5 flex gap-1.5 text-[13px] font-bold leading-snug" style={{ color: PC.deep }}><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />{i.next}</p>}
    </li>
  );
}

function Group({ title, icon: Icon, items, fg, bg, tid, empty }) {
  const [more, setMore] = useState(false);
  const shown = more ? items : items.slice(0, 2);
  return (
    <section className="rounded-[26px] p-4" style={{ background: bg }} data-testid={tid}>
      <h3 className="flex items-center gap-2 text-base font-extrabold" style={{ color: fg }}><Icon className="h-5 w-5" /> {title}</h3>
      <ul className="mt-3 space-y-2">{shown.length ? shown.map((i, k) => <Finding key={k} i={i} />) : <li className="text-sm" style={{ color: PC.muted }}>{empty}</li>}</ul>
      {items.length > 2 && (
        <button onClick={() => setMore(!more)} aria-expanded={more} data-testid={`${tid}-more`} className="mt-2 inline-flex items-center gap-1 text-xs font-extrabold" style={{ color: fg }}>
          {more ? "Show less" : `See ${items.length - 2} more`} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${more ? "rotate-180" : ""}`} />
        </button>)}
    </section>
  );
}

function DimDetail({ dim, status, r }) {
  const st = STATUS[status || "unknown"];
  const related = [...(r.urgent?.items || []), ...r.discuss_vet, ...r.improve, ...r.doing_well].filter((x) => x.domain === r.snapshot.find((s) => s.domain === dim.id)?.title);
  const Icon = dim.icon;
  return (
    <div className="rounded-[24px] bg-white p-4" style={{ border: `1px solid ${PC.line}` }} data-testid="compass-dim-detail">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-2xl" style={{ background: st.bg }}><Icon className="h-5 w-5" style={{ color: st.fg }} /></span>
        <div><p className="font-extrabold" style={{ color: PC.ink }}>{dim.label}</p><p className="text-xs font-extrabold" style={{ color: st.fg }} data-testid="compass-dim-status">{st.label}</p></div>
      </div>
      <ul className="mt-2 space-y-1 text-[13px]" style={{ color: PC.muted }}>
        {related.length ? related.slice(0, 3).map((x, k) => <li key={k}>• {x.reported}</li>) : <li>{status === "unknown" ? "Not answered — that's fine. It's recorded as unknown." : dim.why}</li>}
      </ul>
    </div>
  );
}

function Snapshot({ r }) {
  const statuses = Object.fromEntries(r.snapshot.map((s) => [s.domain, s.status]));
  const first = (r.snapshot.find((s) => ["urgent", "vet", "improve"].includes(s.status)) || r.snapshot[0]).domain;
  const [sel, setSel] = useState(first);
  return (
    <div className="grid items-center gap-6 rounded-[32px] bg-white/70 p-4 sm:p-6 lg:grid-cols-[minmax(0,380px)_1fr]" style={{ border: `1px solid ${PC.line}` }} data-testid="compass-snapshot">
      <InteractiveCompass selected={sel} onSelect={setSel} statuses={statuses} tid="compass-result-dial" />
      <div className="space-y-3">
        <ul className="grid gap-1.5">
          {r.snapshot.map((s, k) => { const st = STATUS[s.status]; const d = DIMS.find((x) => x.id === s.domain);
            return (
              <li key={s.domain}>
                <button onClick={() => setSel(s.domain)} aria-pressed={sel === s.domain} data-testid={`compass-snap-${s.domain}`}
                  className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors" style={{ background: sel === s.domain ? st.bg : "transparent" }}>
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-extrabold text-white" style={{ background: st.fg }}>{k + 1}</span>
                  <span className="flex-1 text-sm font-bold" style={{ color: PC.ink }}>{d?.label || s.title}</span>
                  <span className="rounded-full px-2.5 py-0.5 text-[11px] font-extrabold" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                </button>
              </li>); })}
        </ul>
        <DimDetail dim={DIMS.find((d) => d.id === sel)} status={statuses[sel]} r={r} />
      </div>
    </div>
  );
}

function Plan({ actions }) {
  const cols = [["today", "Start today", "#2F8F5B"], ["soon", "Plan soon", "#B8791F"], ["vet", "Discuss with your vet", "#3D6FB0"]];
  return (
    <details className="group rounded-[24px] bg-white/70 p-4" style={{ border: `1px solid ${PC.line}` }} data-testid="compass-action-plan">
      <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-extrabold" style={{ color: PC.ink }}>Your action plan <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" /></summary>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {cols.map(([k, t, fg]) => (
          <div key={k} data-testid={`compass-plan-${k}`}>
            <p className="text-xs font-extrabold uppercase tracking-wider" style={{ color: fg }}>{t}</p>
            <ul className="mt-1.5 space-y-1 text-[13px]" style={{ color: PC.ink }}>{actions[k].length ? actions[k].slice(0, 3).map((a) => <li key={a} className="flex gap-1.5"><span style={{ color: fg }}>●</span>{a}</li>) : <li style={{ color: PC.muted }}>Nothing needed right now.</li>}</ul>
          </div>
        ))}
      </div>
    </details>
  );
}

export function CompassResults({ r, onPdf, onPrint, onRetake, pdfBusy, children }) {
  const p = r.pet || {};
  const nb = r.next_best_step;
  const btn = "inline-flex min-h-[48px] items-center gap-2 rounded-full px-5 text-sm font-extrabold disabled:opacity-60";
  return (
    <div className="space-y-5" data-testid="compass-results">
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.16em]" style={{ color: "#2F8F5B" }}>Your Pet Compass · {r.assessed_on}</p>
        <h2 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl" style={{ color: PC.ink }}>{p.name || "Your pet"}{p.species ? <span className="text-xl font-bold" style={{ color: PC.muted }}> · {p.species}</span> : null}</h2>
      </div>
      {r.urgent?.show && (
        <div role="alert" className="flex gap-3 rounded-[24px] p-4" style={{ background: "#FBE3DC", color: "#8E2C14" }} data-testid="compass-urgent">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div><p className="font-extrabold">Please contact a veterinarian promptly</p><p className="text-sm">{r.urgent.text}</p>
            <ul className="mt-1 list-disc pl-5 text-sm">{r.urgent.items.map((u) => <li key={u.reported}>{u.reported}</li>)}</ul></div>
        </div>)}
      <Snapshot r={r} />
      {nb && (
        <div className="rounded-[26px] p-5 text-white" style={{ background: PC.deep }} data-testid="compass-next-step">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-emerald-200">Your next best step</p>
          <p className="mt-1 text-xl font-extrabold">{nb.title}</p><p className="mt-1 text-sm text-emerald-50/90">{nb.text}</p>
        </div>)}
      <div className="grid gap-3 lg:grid-cols-3">
        <Group title="You're doing well" icon={CheckCircle2} items={r.doing_well} fg="#2F8F5B" bg="#EAF4EC" tid="compass-doing-well" empty="Answer a few more questions to see strengths." />
        <Group title="Worth a look" icon={Sprout} items={r.improve} fg="#B8791F" bg="#FBF1E1" tid="compass-improve" empty="Nothing flagged from your answers." />
        <Group title="Ask your vet" icon={Stethoscope} items={[...(r.urgent?.items || []), ...r.discuss_vet]} fg="#3D6FB0" bg="#E8EEF8" tid="compass-discuss-vet" empty="Nothing specific — keep up routine check-ups." />
      </div>
      <section className="rounded-[26px] bg-white p-5" style={{ border: "2px solid #2F8F5B" }} data-testid="compass-preventive">
        <p className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.14em]" style={{ color: "#2F8F5B" }}><ShieldCheck className="h-4 w-4" /> Preventive health & diagnostics</p>
        <h3 className="mt-1 text-lg font-extrabold" style={{ color: PC.ink }} data-testid="compass-preventive-headline">{r.preventive.headline}</h3>
        <p className="mt-1 text-sm font-bold" style={{ color: PC.deep }} data-testid="compass-diagnostics-line">{r.preventive.diagnostics}</p>
        <details className="mt-2 text-sm" style={{ color: PC.muted }}><summary className="cursor-pointer text-xs font-bold">What happens at a health review?</summary><p className="mt-1">{r.preventive.exam}</p></details>
        <p className="mt-2 text-xs" style={{ color: PC.muted }}>{r.preventive.rule}</p>
      </section>
      <Plan actions={r.actions} />
      {r.spay_note && <p className="rounded-2xl bg-white/70 p-3 text-[13px]" style={{ color: PC.muted }} data-testid="compass-spay-note">{r.spay_note}</p>}
      {r.unanswered?.length > 0 && <p className="text-xs" style={{ color: PC.muted }} data-testid="compass-unanswered">Not enough information on: {r.unanswered.join(", ")}</p>}
      {children}
      <div className="flex flex-wrap gap-2">
        <button onClick={onPdf} disabled={pdfBusy} data-testid="compass-download-pdf" className={`${btn} text-white`} style={{ background: PC.deep }}><Download className="h-4 w-4" /> Download My Pet Compass</button>
        <button onClick={onPrint} disabled={pdfBusy} data-testid="compass-print" className={`${btn} bg-white`} style={{ border: `1.5px solid ${PC.line}`, color: PC.ink }}><Printer className="h-4 w-4" /> Print</button>
        {onRetake && <button onClick={onRetake} data-testid="compass-retake" className={btn} style={{ color: PC.muted }}><RotateCcw className="h-4 w-4" /> Retake Assessment</button>}
      </div>
      <p className="text-xs" style={{ color: PC.muted }} data-testid="compass-disclaimer">{r.disclaimer}</p>
    </div>
  );
}

export async function openPdf(blobPromise, filename, print) {
  const blob = new Blob([(await blobPromise).data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  if (print) { window.open(url, "_blank"); return; }
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
